/** Eight certificate-depth examples per part for Matrices & Determinants. */

export const LA_MATRICES_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Matrix times vector as a combination of columns",
    setup: "Compute $Ax$ for $A=\\begin{pmatrix}2&-1\\\\0&3\\\\1&4\\end{pmatrix}$ and $x=(3,2)$.",
    steps: [
      { text: "Shapes: $A$ is $3\\times2$ and $x$ is $2\\times1$, so $Ax$ is $3\\times1$.", why: "Inner dimensions must match." },
      { text: "$Ax=3\\,\\text{col}_1+2\\,\\text{col}_2$.", why: "A matrix-vector product is a combination of columns." },
      { text: "$3\\,\\text{col}_1=3(2,0,1)=(6,0,3)$.", why: "Scale the first column." },
      { text: "$2\\,\\text{col}_2=2(-1,3,4)=(-2,6,8)$.", why: "Scale the second column." },
      { text: "Add: first entry $6-2=4$.", why: "Componentwise addition." },
      { text: "Second entry $0+6=6$.", why: "Continue." },
      { text: "Third entry $3+8=11$.", why: "Finish." },
      { text: "So $Ax=(4,6,11)$.", why: "Same answer as row-by-row dot products." },
    ],
    result: "$Ax=(4,6,11)$",
    check: "Row $1$ dotted with $x$: $2(3)+(-1)(2)=4$. Matches.",
    mistake: "Multiplying entry-by-entry as if $A$ and $x$ had the same shape.",
    life: "A camera transform is one matrix-vector product per vertex — the GPU is doing millions of these column combinations every frame.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Matrix product and non-commutativity",
    setup: "For $A=\\begin{pmatrix}1&2\\\\0&1\\end{pmatrix}$ and $B=\\begin{pmatrix}1&0\\\\3&1\\end{pmatrix}$, compute $AB$ and $BA$.",
    steps: [
      { text: "$(AB)_{11}=1(1)+2(3)=7$.", why: "Row $1$ of $A$ dot column $1$ of $B$." },
      { text: "$(AB)_{12}=1(0)+2(1)=2$.", why: "Row $1$ dot column $2$." },
      { text: "$(AB)_{21}=0(1)+1(3)=3$.", why: "Row $2$ dot column $1$." },
      { text: "$(AB)_{22}=0(0)+1(1)=1$.", why: "Row $2$ dot column $2$." },
      { text: "So $AB=\\begin{pmatrix}7&2\\\\3&1\\end{pmatrix}$.", why: "Assemble the block." },
      { text: "Now $(BA)_{11}=1(1)+0(0)=1$ and $(BA)_{12}=1(2)+0(1)=2$.", why: "Repeat with the order swapped." },
      { text: "$(BA)_{21}=3(1)+1(0)=3$, $(BA)_{22}=3(2)+1(1)=7$.", why: "Second row." },
      { text: "$BA=\\begin{pmatrix}1&2\\\\3&7\\end{pmatrix}\\ne AB$.", why: "Matrix multiplication is not commutative." },
    ],
    result: "$AB=\\begin{pmatrix}7&2\\\\3&1\\end{pmatrix}$, $BA=\\begin{pmatrix}1&2\\\\3&7\\end{pmatrix}$",
    check: "Both have determinant $1$, as expected since $\\det(AB)=\\det(BA)=\\det A\\det B$.",
    mistake: "Assuming $AB=BA$ and cancelling matrices as if they were numbers.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Composition of two linear maps",
    setup: "Rotate by $90^\\circ$ counterclockwise, then reflect across the $x$-axis. Find the single matrix.",
    steps: [
      { text: "Rotation by $90^\\circ$: $R=\\begin{pmatrix}0&-1\\\\1&0\\end{pmatrix}$.", why: "Columns are the images of $e_1$ and $e_2$." },
      { text: "Reflection across the $x$-axis: $F=\\begin{pmatrix}1&0\\\\0&-1\\end{pmatrix}$.", why: "Flips the $y$-coordinate." },
      { text: "\"Rotate then reflect\" is $FR$, not $RF$.", why: "The map applied first sits on the right." },
      { text: "$(FR)_{11}=1(0)+0(1)=0$, $(FR)_{12}=1(-1)+0(0)=-1$.", why: "First row." },
      { text: "$(FR)_{21}=0(0)+(-1)(1)=-1$, $(FR)_{22}=0(-1)+(-1)(0)=0$.", why: "Second row." },
      { text: "$FR=\\begin{pmatrix}0&-1\\\\-1&0\\end{pmatrix}$.", why: "The composite matrix." },
      { text: "Test with $e_1=(1,0)$: rotation gives $(0,1)$, reflection gives $(0,-1)$, and $FR\\,e_1=(0,-1)$.", why: "Verify with a basis vector." },
      { text: "Note $\\det(FR)=0\\cdot0-(-1)(-1)=-1$, so this composite is itself a reflection (across $y=-x$).", why: "Negative determinant flips orientation." },
    ],
    result: "$FR=\\begin{pmatrix}0&-1\\\\-1&0\\end{pmatrix}$ — reflection across the line $y=-x$",
    check: "$(1,-1)\\mapsto(1,-1)$, so the line $y=-x$ is fixed, as a reflection axis must be.",
    mistake: "Writing $RF$ for \"rotate first\"; order of composition matters.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Transpose identities",
    setup: "For $A=\\begin{pmatrix}1&2&0\\\\3&-1&4\\end{pmatrix}$, compute $A^{T}A$ and confirm it is symmetric.",
    steps: [
      { text: "$A$ is $2\\times3$, so $A^{T}$ is $3\\times2$ and $A^{T}A$ is $3\\times3$.", why: "Shape bookkeeping first." },
      { text: "$(A^{T}A)_{11}=1^2+3^2=10$.", why: "Column $1$ of $A$ dotted with itself." },
      { text: "$(A^{T}A)_{12}=1(2)+3(-1)=-1$.", why: "Column $1$ dot column $2$." },
      { text: "$(A^{T}A)_{13}=1(0)+3(4)=12$.", why: "Column $1$ dot column $3$." },
      { text: "$(A^{T}A)_{22}=2^2+(-1)^2=5$ and $(A^{T}A)_{23}=2(0)+(-1)(4)=-4$.", why: "Second row entries." },
      { text: "$(A^{T}A)_{33}=0^2+4^2=16$.", why: "Last diagonal entry." },
      { text: "So $A^{T}A=\\begin{pmatrix}10&-1&12\\\\-1&5&-4\\\\12&-4&16\\end{pmatrix}$.", why: "Fill in by symmetry." },
      { text: "$(A^{T}A)^{T}=A^{T}(A^{T})^{T}=A^{T}A$, so symmetry is automatic.", why: "Proof, not luck." },
    ],
    result: "$A^{T}A=\\begin{pmatrix}10&-1&12\\\\-1&5&-4\\\\12&-4&16\\end{pmatrix}$, symmetric",
    check: "Every diagonal entry is a sum of squares, hence nonnegative.",
    mistake: "Claiming $(AB)^{T}=A^{T}B^{T}$; the correct rule reverses the order.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Powers of a matrix and a recurrence",
    setup: "For $A=\\begin{pmatrix}1&1\\\\1&0\\end{pmatrix}$, compute $A^{2}$, $A^{3}$, $A^{4}$ and interpret the entries.",
    steps: [
      { text: "$A^{2}=A\\cdot A$: $(A^2)_{11}=1(1)+1(1)=2$.", why: "Start the product." },
      { text: "$(A^2)_{12}=1(1)+1(0)=1$, $(A^2)_{21}=1(1)+0(1)=1$, $(A^2)_{22}=1$.", why: "Remaining entries." },
      { text: "$A^{2}=\\begin{pmatrix}2&1\\\\1&1\\end{pmatrix}$.", why: "First power." },
      { text: "$A^{3}=A^{2}A=\\begin{pmatrix}3&2\\\\2&1\\end{pmatrix}$.", why: "Multiply again." },
      { text: "$A^{4}=A^{3}A=\\begin{pmatrix}5&3\\\\3&2\\end{pmatrix}$.", why: "One more step." },
      { text: "The entries are Fibonacci numbers: $A^{n}=\\begin{pmatrix}F_{n+1}&F_n\\\\F_n&F_{n-1}\\end{pmatrix}$.", why: "Pattern $1,1,2,3,5$." },
      { text: "Reason: $A(F_n,F_{n-1})^{T}=(F_n+F_{n-1},F_n)^{T}=(F_{n+1},F_n)^{T}$.", why: "The matrix encodes the recurrence." },
      { text: "$\\det A=-1$, so $\\det A^{n}=(-1)^n$, giving $F_{n+1}F_{n-1}-F_n^{2}=(-1)^n$.", why: "A determinant identity for free." },
    ],
    result: "$A^{4}=\\begin{pmatrix}5&3\\\\3&2\\end{pmatrix}$, and $A^{n}$ carries Fibonacci numbers",
    check: "$n=4$: $F_5F_3-F_4^{2}=5(2)-3^{2}=1=(-1)^4$.",
    mistake: "Computing $A^{n}$ by raising each entry to the $n$th power.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Elementary matrices as row operations",
    setup: "Write the row operation $R_2\\to R_2-3R_1$ on a $3\\times3$ matrix as left multiplication, and invert it.",
    steps: [
      { text: "Start from $I_3$ and apply the operation to it.", why: "That is exactly how elementary matrices are built." },
      { text: "Row $2$ of $I$ is $(0,1,0)$; subtracting $3$ times row $1=(1,0,0)$ gives $(-3,1,0)$.", why: "Apply the operation to the identity." },
      { text: "So $E=\\begin{pmatrix}1&0&0\\\\-3&1&0\\\\0&0&1\\end{pmatrix}$.", why: "Other rows unchanged." },
      { text: "Then $EA$ performs the operation on any $3\\times n$ matrix $A$.", why: "Left multiplication acts on rows." },
      { text: "The inverse operation is $R_2\\to R_2+3R_1$.", why: "Undo the subtraction." },
      { text: "So $E^{-1}=\\begin{pmatrix}1&0&0\\\\3&1&0\\\\0&0&1\\end{pmatrix}$.", why: "Flip the sign of the multiplier." },
      { text: "Verify: $EE^{-1}$ has $(2,1)$ entry $-3+3=0$ and ones on the diagonal, so it is $I$.", why: "Confirms the inverse." },
      { text: "$\\det E=1$: adding a multiple of one row to another never changes the determinant.", why: "Key fact for Part 2." },
    ],
    result: "$E=\\begin{pmatrix}1&0&0\\\\-3&1&0\\\\0&0&1\\end{pmatrix}$ with $E^{-1}$ having $+3$ in place of $-3$",
    check: "Applying $E$ to $\\begin{pmatrix}1&1&1\\\\3&4&5\\\\0&0&1\\end{pmatrix}$ turns row $2$ into $(0,1,2)$.",
    mistake: "Multiplying on the right; $AE$ performs a column operation, not a row operation.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Block structure and a linear map's matrix",
    setup: "Find the matrix of $T(x,y,z)=(x+2y,\\,y-z,\\,x+z)$ and compute $T$ of $(1,-1,2)$ two ways.",
    steps: [
      { text: "Column $1$ is $T(e_1)=T(1,0,0)=(1,0,1)$.", why: "Columns are images of the standard basis." },
      { text: "Column $2$ is $T(0,1,0)=(2,1,0)$.", why: "Read the coefficients of $y$." },
      { text: "Column $3$ is $T(0,0,1)=(0,-1,1)$.", why: "Read the coefficients of $z$." },
      { text: "So $M=\\begin{pmatrix}1&2&0\\\\0&1&-1\\\\1&0&1\\end{pmatrix}$.", why: "Assemble columns." },
      { text: "Direct formula: $T(1,-1,2)=(1-2,\\,-1-2,\\,1+2)=(-1,-3,3)$.", why: "Substitute into the definition." },
      { text: "Matrix route: $M(1,-1,2)^{T}$ first entry $=1-2+0=-1$.", why: "Row $1$ dot the vector." },
      { text: "Second entry $=0-1-2=-3$; third entry $=1+0+2=3$.", why: "Rows $2$ and $3$." },
      { text: "Both give $(-1,-3,3)$, so $M$ represents $T$ correctly.", why: "Cross-validation." },
    ],
    result: "$M=\\begin{pmatrix}1&2&0\\\\0&1&-1\\\\1&0&1\\end{pmatrix}$ and $T(1,-1,2)=(-1,-3,3)$",
    check: "$\\det M=1(1)-2(0+1)+0=-1\\ne0$, so $T$ is invertible.",
    mistake: "Writing the coefficients as rows instead of columns.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Trace, symmetry, and a decomposition",
    setup: "Split $A=\\begin{pmatrix}2&5\\\\1&4\\end{pmatrix}$ into symmetric plus skew-symmetric parts and compare traces.",
    steps: [
      { text: "Every square matrix satisfies $A=S+K$ with $S=\\tfrac12(A+A^{T})$ and $K=\\tfrac12(A-A^{T})$.", why: "Standard decomposition." },
      { text: "$A^{T}=\\begin{pmatrix}2&1\\\\5&4\\end{pmatrix}$.", why: "Swap rows and columns." },
      { text: "$A+A^{T}=\\begin{pmatrix}4&6\\\\6&8\\end{pmatrix}$.", why: "Entrywise sum." },
      { text: "$S=\\begin{pmatrix}2&3\\\\3&4\\end{pmatrix}$, which is symmetric.", why: "Halve the sum." },
      { text: "$A-A^{T}=\\begin{pmatrix}0&4\\\\-4&0\\end{pmatrix}$, so $K=\\begin{pmatrix}0&2\\\\-2&0\\end{pmatrix}$.", why: "Halve the difference." },
      { text: "Check $S+K=\\begin{pmatrix}2&5\\\\1&4\\end{pmatrix}=A$.", why: "Decomposition verified." },
      { text: "$\\operatorname{tr}A=2+4=6=\\operatorname{tr}S$, and $\\operatorname{tr}K=0$.", why: "Skew parts always have zero trace." },
      { text: "Also $\\operatorname{tr}(A)=\\operatorname{tr}(A^{T})$, which is why the split preserves the trace.", why: "Explains the pattern." },
    ],
    result: "$A=\\begin{pmatrix}2&3\\\\3&4\\end{pmatrix}+\\begin{pmatrix}0&2\\\\-2&0\\end{pmatrix}$, $\\operatorname{tr}A=6$",
    check: "$K^{T}=-K$ and $S^{T}=S$, exactly as required.",
    mistake: "Forgetting the factor $\\tfrac12$, which makes the parts add to $2A$.",
  },
];

export const LA_MATRICES_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "$2\\times2$ determinant and inverse",
    setup: "For $A=\\begin{pmatrix}4&7\\\\2&6\\end{pmatrix}$, find $\\det A$ and $A^{-1}$.",
    steps: [
      { text: "$\\det A=ad-bc=4(6)-7(2)$.", why: "The $2\\times2$ formula." },
      { text: "$=24-14=10\\ne0$, so $A$ is invertible.", why: "Nonzero determinant." },
      { text: "The adjugate swaps the diagonal and negates the off-diagonal: $\\begin{pmatrix}6&-7\\\\-2&4\\end{pmatrix}$.", why: "Standard $2\\times2$ rule." },
      { text: "$A^{-1}=\\tfrac{1}{\\det A}\\operatorname{adj}A$.", why: "Inverse formula." },
      { text: "$A^{-1}=\\tfrac{1}{10}\\begin{pmatrix}6&-7\\\\-2&4\\end{pmatrix}$.", why: "Divide by $10$." },
      { text: "$=\\begin{pmatrix}0.6&-0.7\\\\-0.2&0.4\\end{pmatrix}$.", why: "Decimal form." },
      { text: "Check $(AA^{-1})_{11}=4(0.6)+7(-0.2)=2.4-1.4=1$.", why: "Diagonal entry of $I$." },
      { text: "$(AA^{-1})_{12}=4(-0.7)+7(0.4)=-2.8+2.8=0$.", why: "Off-diagonal vanishes, so $AA^{-1}=I$." },
    ],
    result: "$\\det A=10$, $A^{-1}=\\tfrac{1}{10}\\begin{pmatrix}6&-7\\\\-2&4\\end{pmatrix}$",
    check: "$\\det(A^{-1})=1/10$, consistent with $\\det(A^{-1})=1/\\det A$.",
    mistake: "Negating the diagonal instead of the off-diagonal entries in the adjugate.",
    life: "Every time software solves a two-equation calibration, it is checking this determinant first — a near-zero value warns that the sensors are nearly redundant.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "$3\\times3$ determinant by cofactor expansion",
    setup: "Compute $\\det\\begin{pmatrix}2&0&1\\\\1&3&2\\\\0&-1&4\\end{pmatrix}$.",
    steps: [
      { text: "Expand along row $1$ because it contains a zero.", why: "Zeros reduce the work." },
      { text: "$\\det=2M_{11}-0\\cdot M_{12}+1\\cdot M_{13}$ with alternating signs $+,-,+$.", why: "Cofactor expansion." },
      { text: "$M_{11}=\\det\\begin{pmatrix}3&2\\\\-1&4\\end{pmatrix}=12+2=14$.", why: "Delete row $1$, column $1$." },
      { text: "The middle term vanishes since the entry is $0$.", why: "No need for $M_{12}$." },
      { text: "$M_{13}=\\det\\begin{pmatrix}1&3\\\\0&-1\\end{pmatrix}=-1-0=-1$.", why: "Delete row $1$, column $3$." },
      { text: "Combine: $\\det=2(14)+1(-1)$.", why: "Signs: $+$ for position $(1,1)$ and $+$ for $(1,3)$." },
      { text: "$=28-1=27$.", why: "Arithmetic." },
      { text: "Since $27\\ne0$, the matrix is invertible and its columns are independent.", why: "Interpretation." },
    ],
    result: "$\\det=27$",
    check: "Expanding along column $1$ instead: $2(14)-1(0\\cdot4-1(-1))=28-1=27$.",
    mistake: "Using signs $+,+,+$ instead of the alternating checkerboard pattern.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Determinant by row reduction",
    setup: "Use elimination to evaluate $\\det\\begin{pmatrix}1&2&3\\\\2&5&8\\\\3&8&14\\end{pmatrix}$.",
    steps: [
      { text: "$R_2\\to R_2-2R_1$ gives $(0,1,2)$; this does not change the determinant.", why: "Adding a multiple of a row is determinant-preserving." },
      { text: "$R_3\\to R_3-3R_1$ gives $(0,2,5)$.", why: "Clear the first column." },
      { text: "Matrix is now $\\begin{pmatrix}1&2&3\\\\0&1&2\\\\0&2&5\\end{pmatrix}$.", why: "Partial echelon form." },
      { text: "$R_3\\to R_3-2R_2$ gives $(0,0,1)$.", why: "Clear the second column." },
      { text: "Upper-triangular form: diagonal $1,1,1$.", why: "Determinant of a triangular matrix is the diagonal product." },
      { text: "No row swaps and no row scalings were used, so no sign or factor corrections apply.", why: "Track the bookkeeping." },
      { text: "$\\det=1\\cdot1\\cdot1=1$.", why: "Product of pivots." },
      { text: "Row reduction costs $O(n^{3})$ versus $O(n!)$ for full cofactor expansion.", why: "Why software always uses elimination." },
    ],
    result: "$\\det=1$",
    check: "Cofactor expansion gives $1(70-64)-2(28-24)+3(16-15)=6-8+3=1$.",
    mistake: "Forgetting that a row swap multiplies the determinant by $-1$ and scaling a row by $k$ multiplies it by $k$.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Inverse by Gauss–Jordan",
    setup: "Invert $A=\\begin{pmatrix}1&2\\\\3&5\\end{pmatrix}$ using the augmented matrix $[A\\mid I]$.",
    steps: [
      { text: "Start with $\\left(\\begin{array}{cc|cc}1&2&1&0\\\\3&5&0&1\\end{array}\\right)$.", why: "Augment with the identity." },
      { text: "$R_2\\to R_2-3R_1$: $\\left(\\begin{array}{cc|cc}1&2&1&0\\\\0&-1&-3&1\\end{array}\\right)$.", why: "Create a zero below the first pivot." },
      { text: "$R_2\\to -R_2$: second row becomes $(0,1\\mid 3,-1)$.", why: "Make the pivot $1$." },
      { text: "$R_1\\to R_1-2R_2$: first row becomes $(1,0\\mid 1-6,\\,0+2)=(1,0\\mid -5,2)$.", why: "Clear above the pivot." },
      { text: "Left block is now $I$, so the right block is $A^{-1}$.", why: "Gauss–Jordan endpoint." },
      { text: "$A^{-1}=\\begin{pmatrix}-5&2\\\\3&-1\\end{pmatrix}$.", why: "Read it off." },
      { text: "Cross-check with the formula: $\\det A=5-6=-1$, $\\operatorname{adj}A=\\begin{pmatrix}5&-2\\\\-3&1\\end{pmatrix}$, divided by $-1$.", why: "Two independent routes." },
      { text: "$AA^{-1}$ has $(1,1)$ entry $1(-5)+2(3)=1$ and $(1,2)$ entry $1(2)+2(-1)=0$.", why: "Verified." },
    ],
    result: "$A^{-1}=\\begin{pmatrix}-5&2\\\\3&-1\\end{pmatrix}$",
    check: "$\\det A^{-1}=5-6=-1=1/\\det A$.",
    mistake: "Applying a row operation to the left block but not to the augmented right block.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Cramer's rule on a $3\\times3$ system",
    setup: "Solve $x+y+z=6$, $2y+5z=-4$, $2x+5y-z=27$ by determinants.",
    steps: [
      { text: "Coefficient matrix $A=\\begin{pmatrix}1&1&1\\\\0&2&5\\\\2&5&-1\\end{pmatrix}$, right side $b=(6,-4,27)$.", why: "Set up." },
      { text: "$\\det A=1(2(-1)-5\\cdot5)-1(0(-1)-5\\cdot2)+1(0\\cdot5-2\\cdot2)$.", why: "Expand along row $1$." },
      { text: "$=1(-27)-1(-10)+1(-4)=-27+10-4=-21$.", why: "Nonzero, so a unique solution exists." },
      { text: "$\\det A_1$ replaces column $1$ by $b$: $\\det\\begin{pmatrix}6&1&1\\\\-4&2&5\\\\27&5&-1\\end{pmatrix}=6(-27)-1(4-135)+1(-20-54)$.", why: "Cramer numerator for $x$." },
      { text: "$=-162+131-74=-105$, so $x=-105/-21=5$.", why: "Divide by $\\det A$." },
      { text: "$\\det A_2=\\det\\begin{pmatrix}1&6&1\\\\0&-4&5\\\\2&27&-1\\end{pmatrix}=1(4-135)-6(0-10)+1(0+8)=-131+60+8=-63$, so $y=3$.", why: "Second unknown." },
      { text: "$\\det A_3=\\det\\begin{pmatrix}1&1&6\\\\0&2&-4\\\\2&5&27\\end{pmatrix}=1(54+20)-1(0+8)+6(0-4)=74-8-24=42$, so $z=-2$.", why: "Third unknown." },
      { text: "Solution $(x,y,z)=(5,3,-2)$.", why: "Collect the three quotients." },
    ],
    result: "$(x,y,z)=(5,3,-2)$",
    check: "Equation $3$: $2(5)+5(3)-(-2)=10+15+2=27$. Correct.",
    mistake: "Replacing a row instead of a column when building $A_i$.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Determinant of a product, transpose, and scalar multiple",
    setup: "Given $\\det A=3$ and $\\det B=-2$ for $3\\times3$ matrices, evaluate $\\det(2A)$, $\\det(A^{T}B)$, $\\det(A^{-1}B^{2})$.",
    steps: [
      { text: "Scalar rule: $\\det(cA)=c^{n}\\det A$ with $n=3$.", why: "Each of the three rows is scaled." },
      { text: "$\\det(2A)=2^{3}(3)=24$.", why: "Apply the rule." },
      { text: "Transpose rule: $\\det(A^{T})=\\det A=3$.", why: "Transposing never changes the determinant." },
      { text: "Product rule: $\\det(A^{T}B)=\\det(A^{T})\\det B=3(-2)=-6$.", why: "Multiplicative property." },
      { text: "Inverse rule: $\\det(A^{-1})=1/\\det A=1/3$.", why: "Follows from $AA^{-1}=I$." },
      { text: "$\\det(B^{2})=(\\det B)^{2}=4$.", why: "Product rule applied twice." },
      { text: "$\\det(A^{-1}B^{2})=\\tfrac13(4)=\\tfrac43$.", why: "Combine." },
      { text: "All three are nonzero, so each product matrix is invertible.", why: "Interpretation." },
    ],
    result: "$\\det(2A)=24$, $\\det(A^{T}B)=-6$, $\\det(A^{-1}B^{2})=\\tfrac43$",
    check: "$\\det(A^{-1}B^{2})\\det(A)=4=\\det(B^{2})$, consistent.",
    mistake: "Writing $\\det(2A)=2\\det A$; the exponent is the matrix size.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Singular matrix: find the parameter",
    setup: "For which $k$ is $A=\\begin{pmatrix}1&2&3\\\\2&k&6\\\\1&1&k\\end{pmatrix}$ singular?",
    steps: [
      { text: "Singular means $\\det A=0$.", why: "Definition." },
      { text: "Expand along row $1$: $\\det=1(k\\cdot k-6\\cdot1)-2(2k-6)+3(2-k)$.", why: "Cofactor expansion with the parameter carried along." },
      { text: "First term: $k^{2}-6$.", why: "Minor $\\begin{pmatrix}k&6\\\\1&k\\end{pmatrix}$." },
      { text: "Second term: $-2(2k-6)=-4k+12$.", why: "Sign is minus at position $(1,2)$." },
      { text: "Third term: $3(2-k)=6-3k$.", why: "Minor $\\begin{pmatrix}2&k\\\\1&1\\end{pmatrix}$." },
      { text: "Sum: $k^{2}-6-4k+12+6-3k=k^{2}-7k+12$.", why: "Collect like terms." },
      { text: "Factor: $(k-3)(k-4)=0$, so $k=3$ or $k=4$.", why: "Solve the quadratic." },
      { text: "At $k=3$: rows give $R_3\\cdot? $ — check $(1,2,3)+(1,1,3)$ versus $(2,3,6)$; the rank drops to $2$, confirming singularity.", why: "Sanity check of the algebra." },
    ],
    result: "$A$ is singular exactly when $k=3$ or $k=4$",
    check: "At $k=5$: $\\det=25-35+12=2\\ne0$, so the matrix is invertible there.",
    mistake: "Setting individual entries to zero instead of the determinant.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Inverse of a $3\\times3$ matrix via adjugate",
    setup: "Invert $A=\\begin{pmatrix}1&0&2\\\\0&1&3\\\\1&1&0\\end{pmatrix}$ using cofactors.",
    steps: [
      { text: "$\\det A=1(1\\cdot0-3\\cdot1)-0+2(0\\cdot1-1\\cdot1)=-3-2=-5$.", why: "Nonzero, so the inverse exists." },
      { text: "Cofactors of row $1$: $C_{11}=-3$, $C_{12}=-(0-3)=3$, $C_{13}=0-1=-1$.", why: "Signed minors." },
      { text: "Row $2$: $C_{21}=-(0-2)=2$, $C_{22}=0-2=-2$, $C_{23}=-(1-0)=-1$.", why: "Continue the checkerboard signs." },
      { text: "Row $3$: $C_{31}=0-2=-2$, $C_{32}=-(3-0)=-3$, $C_{33}=1-0=1$.", why: "Last row of cofactors." },
      { text: "The adjugate is the transpose of the cofactor matrix: $\\operatorname{adj}A=\\begin{pmatrix}-3&2&-2\\\\3&-2&-3\\\\-1&-1&1\\end{pmatrix}$.", why: "Transpose, not the raw cofactor matrix." },
      { text: "$A^{-1}=\\tfrac{1}{-5}\\operatorname{adj}A=\\tfrac{1}{5}\\begin{pmatrix}3&-2&2\\\\-3&2&3\\\\1&1&-1\\end{pmatrix}$.", why: "Divide by the determinant." },
      { text: "Check row $1$ of $AA^{-1}$: $\\tfrac15\\bigl(1(3)+0(-3)+2(1)\\bigr)=\\tfrac55=1$.", why: "Diagonal entry." },
      { text: "Off-diagonal: $\\tfrac15\\bigl(1(-2)+0(2)+2(1)\\bigr)=0$.", why: "Confirms $AA^{-1}=I$." },
    ],
    result: "$A^{-1}=\\tfrac{1}{5}\\begin{pmatrix}3&-2&2\\\\-3&2&3\\\\1&1&-1\\end{pmatrix}$",
    check: "$\\det(A^{-1})=-1/5$, matching $1/\\det A$.",
    mistake: "Skipping the transpose step and dividing the cofactor matrix directly.",
  },
];
